import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { paths } from "../../../routes/paths";
import { useClaims } from "../../auth/useClaims";
import { useBranchFilter } from "../../branchFilter/useBranchFilter";
import { useInstallationOrderPayments } from "../useInstallationOrderPayments";
import { money, sumBy } from "../dashboardHelpers";
import { BRAND } from "../chartColors";
import DashboardSectionCard from "../components/DashboardSectionCard";

const PAGE = 8;

/**
 * New installations - order value against what has been collected for them.
 * The one-time half of "expected vs collected"; the monthly half (guards,
 * alarms) is GuardedCustomersMissedPaymentsCard, and the two are never summed:
 * a monthly rate and a one-off bill are different kinds of money.
 *
 * Only receipts tagged "Installation" count as collected. Anything else the
 * same customer paid is shown beside it as "other receipts" - a prompt for the
 * supervisor to re-tag a mis-filed installation payment, never silently added.
 */
const InstallationOrdersVsCollectedCard = () => {
  // Whole-branch figures only - with own-orders or own-receipts scope the
  // balance would be wrong in a way nothing on the card could reveal.
  const { permissions } = useClaims();
  const canView = permissions.includes("Sales.ViewAll") && permissions.includes("Collections.ViewAll");

  const { selectedBranchId } = useBranchFilter();
  const branchId = selectedBranchId ?? undefined;
  const { customers, unlinked, isLoading, isError } = useInstallationOrderPayments({ branchId, skip: !canView });
  const [showAll, setShowAll] = useState(false);

  const sorted = useMemo(
    () => customers.slice().sort((a, b) => b.balance - a.balance || b.orderValue - a.orderValue),
    [customers]
  );

  if (!canView) return null;

  const visible = showAll ? sorted : sorted.slice(0, PAGE);
  const totalValue = sumBy(customers, "orderValue");
  const totalCollected = sumBy(customers, "collected");

  return (
    <div className="col-12 col-xl-6">
      <DashboardSectionCard
        title="New Installations - Order Value vs Collected"
        headerRight="Since Sep 26"
        isLoading={isLoading}
        isEmpty={!isError && customers.length === 0 && unlinked.count === 0}
        emptyText="No installation orders since September 2026."
      >
        {isError ? (
          <p className="text-danger small mb-0">Could not load sales orders or collections.</p>
        ) : (
          <>
            <p className="small text-muted mb-2">
              Ordered <strong>{money(totalValue)}</strong> · Collected <strong className="text-success">{money(totalCollected)}</strong> ·
              Balance <strong className="text-danger">{money(Math.max(0, totalValue - totalCollected))}</strong>
            </p>

            <ul className="list-unstyled mb-0">
              {visible.map((c) => {
                const pct = c.orderValue > 0 ? Math.min(100, (c.collected / c.orderValue) * 100) : 0;
                return (
                  <li key={c.key} className="py-2 border-bottom">
                    <div className="d-flex justify-content-between align-items-start gap-2 small mb-1">
                      <div style={{ minWidth: 0 }}>
                        <Link
                          to={paths.collectionsFiltered({ branchId: c.branchId, custId: c.custId, custName: c.custName, dateFrom: "2026-09-01" })}
                          className="fw-semibold text-decoration-none d-block text-truncate"
                          title={`See ${c.custName}'s collections`}
                        >
                          {c.custName || `Customer ${c.custId}`}
                        </Link>
                        <span className="text-muted">
                          {c.branchCode} · {c.orderCount} order{c.orderCount === 1 ? "" : "s"}
                        </span>
                      </div>
                      <div className="text-end" style={{ flexShrink: 0 }}>
                        <div>
                          {money(c.collected)} <span className="text-muted">of {money(c.orderValue)}</span>
                        </div>
                        {c.balance > 0 ? (
                          <span className="text-danger fw-semibold">Balance {money(c.balance)}</span>
                        ) : (
                          <span className="text-success fw-semibold">Fully collected</span>
                        )}
                      </div>
                    </div>
                    <div className="progress" style={{ height: 6 }}>
                      <div className="progress-bar" style={{ width: `${pct}%`, backgroundColor: BRAND }} />
                    </div>
                    {c.balance > 0 && c.otherReceipts > 0 && (
                      <div className="text-muted fst-italic mt-1" style={{ fontSize: 11.5 }}>
                        {money(c.otherReceipts)} more received from this customer under other service types - not counted here.
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>

            {sorted.length > PAGE && (
              <button type="button" className="btn btn-link btn-sm px-0 mt-2" onClick={() => setShowAll((v) => !v)}>
                {showAll ? "Show fewer" : `Show all ${sorted.length}`}
              </button>
            )}

            {unlinked.count > 0 && (
              <p className="small text-muted mb-0 mt-2">
                {unlinked.count} more order{unlinked.count === 1 ? "" : "s"} ({money(unlinked.amount)}) not linked to a customer record - receipts cannot be matched.
              </p>
            )}
          </>
        )}
      </DashboardSectionCard>
    </div>
  );
};

export default InstallationOrdersVsCollectedCard;
